import {
  renderAbsenceDate,
  renderAbsenceInfo,
} from "../../../utils/absenceFormatters";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  fetchAbsences,
  createAbsence,
  fullUpdateAbsence,
  fetchWorkerProfile,
  updateAbsenceStatus,
  fetchAbsenceBalance,
  fetchHolidays,
  deleteAbsence,
} from "../../../redux/slices/absenceSlice";
import ConfirmationModal from "../../reusableComps/ConfirmationModal/ConfirmationModal";
import RequestAbsenceModal from "../RequestAbsenceModal/RequestAbsenceModal";
import UserAbsenceModal from "../RequestUserAbsenceModal/RequestUserAbsenceModal";
import YearlyCalendar from "../../calendar/YearlyCalendar/YearlyCalendar";
import { Container, Col, Row, Button, Form } from "react-bootstrap";
import { FaAngleRight, FaClock } from "react-icons/fa";

import formatMinutesToReadable from "../../reusableComps/formatDays/formatDays";
import isOverlappingAbsence from "../../reusableComps/isOverlappingAbsence/isOverlappingAbsence";
import HeaderMobile from "../../reusableComps/HeaderMobile/HeaderMobile";
import "./ManageAbsencesMobile.css";
import "../../../styles/theme.css";
import AutocompleteSelect from "../../autocomplete/AutocompleteSelect/AutocompleteSelect";

const getFullName = (obj, t) => {
  if (!obj) return t("components.ManageAbsencesMobile.unknown");
  const fullName = `${obj.first_name || ""} ${obj.last_name || ""}`.trim();
  return fullName || obj.user_name || t("components.ManageAbsencesMobile.unknown");
};

const AbsencesPage = () => {
  const { t, i18n } = useTranslation();
  const holidayLanguage = i18n.language?.startsWith("pt") ? "PT" : "EN";
  const dispatch = useDispatch();
  const [showCoworkersInCalendar, setShowCoworkersInCalendar] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [workerHolidays, setWorkerHolidays] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const [myAbsencesFilter, setMyAbsencesFilter] = useState("All");

  const [selectedUser, setSelectedUser] = useState(null);

  const [showUserDetailModal, setShowUserDetailModal] = useState(false);

  const {
    items: absences,
    isLoading,
    error: _error,
    balance,
    selectedWorkerProfile,
  } = useSelector((state) => state.absences);
  const [confirmAction, setConfirmAction] = useState({
    show: false,
    title: "",
    message: "",
    showReason: false,
    variant: "primary",
    onConfirm: () => { },
  });

  const openAcceptConfirm = (id) => {
    setConfirmAction({
      show: true,
      title: t("components.ManageAbsencesMobile.accept_absence"),
      message: t("components.ManageAbsencesMobile.confirm_approve_msg"),
      showReason: false,
      variant: "success",
      onConfirm: () => handleAcceptAbsence(id),
    });
  };

  const openRejectConfirm = (id) => {
    setConfirmAction({
      show: true,
      title: t("components.ManageAbsencesMobile.reject_absence"),
      message: t("components.ManageAbsencesMobile.reject_reason_msg"),
      showReason: true,
      variant: "danger",
      onConfirm: (reason) => handleRejectAbsence(id, reason),
    });
  };

  const openDeleteConfirm = (id) => {
    setConfirmAction({
      show: true,
      title: t("components.ManageAbsencesMobile.cancel_request"),
      message: t("components.ManageAbsencesMobile.confirm_cancel_msg"),
      showReason: false,
      variant: "danger",
      onConfirm: () => executeDeleteAbsence(id),
    });
  };

  const [coworkersFilter, setCoworkersFilter] = useState("All");
  const [coworkerNameFilter, setCoworkerNameFilter] = useState("All");
  const [selectedPendingAbsence, setSelectedPendingAbsence] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [absenceToEdit, setAbsenceToEdit] = useState(null);
  const [validationError, setValidationError] = useState("");

  const normalizedUserProfile = selectedWorkerProfile
    ? {
      ...selectedWorkerProfile,
      display_name: getFullName(selectedWorkerProfile, t),
    }
    : null;

  const handleShowForUser = (userId) => {
    setSelectedUser(userId);

    setNewAbsence((prev) => ({
      ...prev,
      user_id: userId,
    }));
    handleShow();
  };
  const initialAbsence = {
    start_date: "",
    end_date: "",
    remaining_minutes: "",
    reason: "",
    absence_type: "vacation",
    is_half_day: false,
    is_multiple_days: true,
    start_time: "",
    end_time: "",
    total_minutes: 480,
  };

  const [newAbsence, setNewAbsence] = useState(initialAbsence);
  const user = useSelector((state) => state.auth.user);

  const role = user?.role?.name;

  const isAdmin = role === "Super Admin" || role === "Manager";
  const [filters, _setFilters] = useState({
    order: [],
    status: [],
  });

  const filteredAbsences = useMemo(() => {
    if (!absences) return [];

    return absences.filter((abs) => {
      if (!filters.status || filters.status.length === 0) return true;
      return filters.status.includes(abs.status);
    });
  }, [absences, filters]);

  const sortedAbsences = useMemo(() => {
    if (!filteredAbsences) return [];

    return [...filteredAbsences].sort((a, b) => {
      if (filters.order.includes("Cronológica Inversa")) {
        return new Date(b.start_date) - new Date(a.start_date);
      }
      return new Date(a.start_date) - new Date(b.start_date);
    });
  }, [filteredAbsences, filters.order]);

  const uniqueCoworkers = Array.from(
    new Map(
      sortedAbsences
        .filter((abs) => abs.user_id !== user?.id)
        .map((abs) => [
          String(abs.user_id),
          {
            id: abs.user_id,
            name: getFullName(abs, t),
            email: abs.user?.email || abs.user_email || "",
            profile_picture: abs.user?.avatar || abs.avatar || null,
            sublabel:
              abs.user?.sector || abs.sector || abs.department || t("components.ManageAbsencesMobile.worker_default"),
          },
        ]),
    ).values(),
  );
  const myAbsences = sortedAbsences.filter((abs) => abs.user_id === user?.id);
  const today = new Date();

  const activeAbsences = myAbsences.filter((abs) => {
    const end = new Date(abs.end_date);
    const isOngoingOrFuture = end >= today;
    const matchesFilter =
      myAbsencesFilter === "All" ||
      abs.status.toLowerCase() === myAbsencesFilter.toLowerCase();

    return isOngoingOrFuture && matchesFilter;
  });

  const coworkerAbsences = sortedAbsences.filter((abs) => {
    const end = new Date(abs.end_date);

    if (end < today) return false;

    if (abs.user_id === user?.id) return false;

    if (isAdmin) {
      const matchesStatus =
        coworkersFilter === "All" ||
        abs?.status?.toLowerCase() === coworkersFilter.toLowerCase();
      const _absUserName = abs?.user_name || t("components.ManageAbsencesMobile.unknown");
      const matchesName =
        coworkerNameFilter === "All" ||
        String(abs.user_id) === String(coworkerNameFilter);
      return matchesStatus && matchesName;
    } else {
      return abs?.status?.toLowerCase() === "accepted";
    }
  });
  const historyAbsences = myAbsences.filter((abs) => {
    const end = new Date(abs.end_date);
    return end < today && abs.status === "Accepted";
  });

  const myAbsenceEvents = myAbsences.map((abs) => ({
    id: `absence-${abs.id}`,
    title: abs.absence_type,
    start: new Date(abs.start_date),
    end: new Date(abs.end_date),
    allDay: !abs.is_half_day,
    type: "absence",
    color: "#0E46A3",
  }));

  const coworkerAbsenceEvents = showCoworkersInCalendar
    ? coworkerAbsences.map((abs) => ({
      id: `absence-${abs.id}`,

      title: `${abs.user_name} (${abs.absence_type})`,
      start: new Date(abs.start_date),
      end: new Date(abs.end_date),
      allDay: !abs.is_half_day,
      type: "coworker_absence",
      color: "rgba(14, 70, 163, 0.4)",
    }))
    : [];

  const _calendarAbsences = [...myAbsenceEvents, ...coworkerAbsenceEvents];

  const handleShow = () => {
    setEditMode(false);
    setAbsenceToEdit(null);
    setNewAbsence(initialAbsence);
    setValidationError("");
    setShowModal(true);
  };

  const handleClose = () => {
    setNewAbsence(initialAbsence);
    setShowModal(false);
    setEditMode(false);
    setAbsenceToEdit(null);
    setValidationError("");
  };

  const handleMyAbsenceClick = async (absence) => {
    try {
      setSelectedUser(user.id);
      setSelectedPendingAbsence(absence.id);

      await dispatch(fetchWorkerProfile(user.id)).unwrap();
      await loadWorkerHolidays(user.id);
      setShowUserDetailModal(true);
    } catch (err) {
      console.error("Error opening modal", err);
    }
  };

  const handleWorkerClick = async (absence) => {
    try {
      setSelectedUser(absence.user_id);
      setSelectedPendingAbsence(absence.id);
      await dispatch(fetchWorkerProfile(absence.user_id)).unwrap();
      await loadWorkerHolidays(absence.user_id);
      setShowUserDetailModal(true);
    } catch (err) {
      console.error("Error fetching worker profile:", err);
    }
  };

  const isPastAbsence = (absence) => {
    return new Date(absence.end_date) < new Date();
  };

  const handleAcceptAbsence = async (absenceId) => {
    try {
      await dispatch(
        updateAbsenceStatus({ absenceId, status: "Accepted" }),
      ).unwrap();
      if (selectedUser) {
        await dispatch(fetchWorkerProfile(selectedUser)).unwrap();
      }
      dispatch(fetchAbsences());

      setConfirmAction((prev) => ({ ...prev, show: false }));
      setShowUserDetailModal(false);
      setSelectedPendingAbsence(null);
    } catch (err) {
      console.error("Error accepting absence:", err);
    }
  };

  const handleRejectAbsence = async (absenceId, reason) => {
    try {
      await dispatch(
        updateAbsenceStatus({
          absenceId,
          status: "Rejected",
          rejection_reason: reason,
        }),
      ).unwrap();

      dispatch(fetchAbsences());

      setConfirmAction((prev) => ({ ...prev, show: false }));
      setShowUserDetailModal(false);
      setSelectedPendingAbsence(null);
    } catch (_err) {
      console.error("Error rejecting absence", _err);
    }
  };

  const executeDeleteAbsence = async (id) => {
    try {
      await dispatch(deleteAbsence(id)).unwrap();

      setConfirmAction((prev) => ({ ...prev, show: false }));
      setShowUserDetailModal(false);

      dispatch(fetchAbsences());
      fetchUserBalance();
    } catch (err) {
      console.error("Error deleting absence:", err);
    }
  };

  const loadHolidays = useCallback(async () => {
    try {
      const data = await dispatch(fetchHolidays({ year: currentYear, languageIsoCode: holidayLanguage })).unwrap();
      setHolidays(data);
    } catch (err) {
      console.error("Error loading holidays:", err);
    }
  }, [currentYear, holidayLanguage, dispatch]);

  const loadWorkerHolidays = useCallback(async (userId) => {
    try {
      const data = await dispatch(fetchHolidays({ year: currentYear, userId, languageIsoCode: holidayLanguage })).unwrap();
      setWorkerHolidays(data);
    } catch (err) {
      console.error("Error loading worker holidays:", err);
    }
  }, [currentYear, holidayLanguage, dispatch]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const fetchUserBalance = useCallback(async () => {
    try {
      await dispatch(fetchAbsenceBalance()).unwrap();
    } catch (err) {
      console.error("Error fetching balance", err);
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAbsences());
    fetchUserBalance();
  }, [dispatch, fetchUserBalance]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAbsence((prev) => ({ ...prev, [name]: value }));
  };

  const handleRequestAbsence = (
    e,
    useVacationDays,
    forceRequest = false,
    _documentFile = null,
  ) => {
    e.preventDefault();

    const editingId = editMode && absenceToEdit ? absenceToEdit.id : null;
    if (isOverlappingAbsence(newAbsence, absences, editingId)) {
      setValidationError(
        t("components.ManageAbsencesMobile.overlap_error"),
      );
      return;
    } else {
      setValidationError("");
    }

    // absence_type must be sent as-is (the canonical key, e.g. "vacation"),
    // never capitalized/reformatted here — the backend compares it directly
    // against the organization's configured absence type keys.
    let formattedAbsence = {
      user_id: selectedUser || user.id,
      start_date: newAbsence.start_date,
      end_date: newAbsence.end_date,
      reason: newAbsence.reason,
      absence_type: newAbsence.absence_type,
      remaining_minutes: balance ? balance.available_minutes : undefined,
      is_half_day: newAbsence.is_half_day || false,
      is_multiple_days: newAbsence.is_multiple_days || false,
      start_time: newAbsence.start_time || null,
      end_time: newAbsence.end_time || null,
      force_request: forceRequest,
    };

    if (
      newAbsence.is_half_day &&
      newAbsence.start_time &&
      newAbsence.end_time
    ) {
      formattedAbsence.start_date = new Date(
        `${newAbsence.start_date}T${newAbsence.start_time}`,
      ).toISOString();
      formattedAbsence.end_date = new Date(
        `${newAbsence.start_date}T${newAbsence.end_time}`,
      ).toISOString();
    } else {
      formattedAbsence.start_date = new Date(
        newAbsence.start_date,
      ).toISOString();
      formattedAbsence.end_date = new Date(newAbsence.end_date).toISOString();
    }

    if (editMode && absenceToEdit) {
      dispatch(
        fullUpdateAbsence({
          absenceId: absenceToEdit.id,
          updateData: formattedAbsence,
        }),
      ).then((action) => {
        if (fullUpdateAbsence.rejected.match(action)) return;
        handleClose();
        dispatch(fetchAbsences());
        fetchUserBalance();
      });
    } else {
      dispatch(createAbsence(formattedAbsence)).then((action) => {
        if (createAbsence.rejected.match(action)) return;
        setNewAbsence(initialAbsence);
        handleClose();
        fetchUserBalance();
      });
    }
  };

  return (
    <div
      className="absences-wrapper"
      style={{ height: "100%", overflowY: "auto", paddingBottom: "50px" }}
    >
      <Container fluid className="pt-4 px-4">
        <div className="d-lg-none mb-3">
          <HeaderMobile showArrow={false} showDelete={false} showEdit={false}>
            {t("components.ManageAbsencesMobile.absences_title")}
          </HeaderMobile>
        </div>

        <Row className="g-4 mb-4">
          <Col lg={8} xl={9}>
            <div className="d-flex justify-content-end mb-2">
              <Form.Check
                type="switch"
                id="coworker-calendar-filter"
                label={t("components.ManageAbsencesMobile.show_coworkers_switch")}
                className="txt-main"
                checked={showCoworkersInCalendar}
                onChange={(e) => setShowCoworkersInCalendar(e.target.checked)}
              />
            </div>
            <div className="dashboard-card overflow-hidden absence-page">
              <YearlyCalendar
                absences={myAbsences}
                user={user}
                holidays={holidays}
                currentYear={currentYear}
                onYearChange={setCurrentYear}
              />
            </div>
          </Col>

          <Col lg={4} xl={3} className="d-flex flex-column gap-3">
            <Button
              className="w-100 py-3 fw-bold action-btn"
              onClick={handleShow}
            >
              {t("components.ManageAbsencesMobile.request_absence_btn")}
            </Button>

            <div className="dashboard-card p-3">
              <div className="d-flex justify-content-between align-items-center border-bottom border-secondary pb-2 mb-3">
                <FaAngleRight
                  className="txt-main opacity-50"
                  style={{ transform: "rotate(180deg)" }}
                />
                <div className="text-center">
                  <h6 className="txt-subtle m-0 fw-bold">{t("components.ManageAbsencesMobile.vacation_counter")}</h6>
                  <small className="txt-main-50" style={{ fontSize: "10px" }}>
                    {t("components.ManageAbsencesMobile.year_range", { year: currentYear })}
                  </small>
                </div>
                <FaAngleRight className="txt-main opacity-50" />
              </div>

              <div className="d-flex justify-content-around text-center txt-main">
                <div>
                  <div className="fs-4 fw-bold">
                    {balance
                      ? formatMinutesToReadable(balance.total_minutes)
                      : "22"}
                  </div>
                  <div style={{ fontSize: "11px", opacity: 0.7 }}>
                    {t("components.ManageAbsencesMobile.acquired")}
                  </div>
                </div>
                <div>
                  <div className="fs-4 fw-bold">
                    {balance
                      ? formatMinutesToReadable(balance.available_minutes)
                      : "0"}
                  </div>
                  <div style={{ fontSize: "11px", opacity: 0.7 }}>
                    {t("components.ManageAbsencesMobile.available")}
                  </div>
                </div>
                <div>
                  <div className="fs-4 fw-bold">
                    {balance
                      ? formatMinutesToReadable(balance.used_minutes)
                      : "22"}
                  </div>
                  <div style={{ fontSize: "11px", opacity: 0.7 }}>
                    {t("components.ManageAbsencesMobile.used")}
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard-card p-3 flex-grow-1">
              <h6 className="txt-main border-bottom border-secondary pb-2 mb-3">
                {t("components.ManageAbsencesMobile.absence_history")}
              </h6>
              <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                {historyAbsences.length > 0 ? (
                  historyAbsences.map((absence) => (
                    <div
                      key={absence.id}
                      className="absence-row accepted d-flex justify-content-between align-items-center mb-2"
                    >
                      <div>
                        <h6 className="txt-main m-0 fw-bold">
                          {renderAbsenceDate(absence)}
                        </h6>
                        <small className="txt-main-50">
                          {renderAbsenceInfo(absence, formatMinutesToReadable)}
                        </small>
                      </div>
                      <div className="status-pill accepted">
                        {absence.status}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="txt-main-50">{t("components.ManageAbsencesMobile.no_history")}</p>
                )}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-4">
          <Col lg={6}>
            <div className="d-flex flex-column h-100">
              <div className="d-flex justify-content-between align-items-center mb-3 px-2 border-bottom border-secondary pb-2">
                <h5 className="txt-main m-0">{t("components.ManageAbsencesMobile.my_absences")}</h5>
                <Form.Select
                  size="sm"
                  className="bg-dark txt-main border-secondary shadow-none w-auto"
                  value={myAbsencesFilter}
                  onChange={(e) => setMyAbsencesFilter(e.target.value)}
                >
                  <option value="All">{t("components.ManageAbsencesMobile.all")}</option>
                  <option value="Pending">{t("components.ManageAbsencesMobile.pending")}</option>
                  <option value="Accepted">{t("components.ManageAbsencesMobile.accepted")}</option>
                  <option value="Rejected">{t("components.ManageAbsencesMobile.rejected")}</option>
                </Form.Select>
              </div>

              <div
                className="list-container custom-scroll flex-grow-1"
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                {isLoading && <p className="txt-main">{t("components.ManageAbsencesMobile.loading")}</p>}
                {activeAbsences.map((absence) => (
                  <div
                    key={absence.id}
                    onClick={() => handleMyAbsenceClick(absence)}
                    className={`absence-row ${absence.status.toLowerCase()} d-flex justify-content-between align-items-center mb-2`}
                    style={{ cursor: "pointer" }}
                  >
                    <div>
                      <h6 className="txt-main m-0 fw-bold">
                        {renderAbsenceDate(absence)}
                      </h6>
                      <small className="txt-main-50">
                        <FaClock className="me-1" />{" "}
                        {renderAbsenceInfo(absence, formatMinutesToReadable)}
                      </small>
                    </div>
                    <div
                      className={`status-pill ${absence.status.toLowerCase()}`}
                    >
                      {absence.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="d-flex flex-column h-100">
              <div className="d-flex justify-content-between align-items-center mb-3 px-2 border-bottom border-secondary pb-2">
                <h5 className="txt-main m-0">{t("components.ManageAbsencesMobile.workers_absences")}</h5>
                <div className="d-flex gap-2">
                  {isAdmin && (
                    <>
                      <AutocompleteSelect
                        type="users"
                        variant="lg"
                        data={uniqueCoworkers}
                        value={coworkerNameFilter}
                        onSelect={(sel) =>
                          setCoworkerNameFilter(sel ? sel.value : "All")
                        }
                        placeholder={t("components.ManageAbsencesMobile.worker_placeholder")}
                      />
                      <Form.Select
                        size="sm"
                        className="bg-dark txt-main border-secondary shadow-none w-auto"
                        value={coworkersFilter}
                        onChange={(e) => setCoworkersFilter(e.target.value)}
                      >
                        <option value="All">{t("components.ManageAbsencesMobile.status_placeholder")}</option>
                        <option value="Pending">{t("components.ManageAbsencesMobile.pending")}</option>
                        <option value="Accepted">{t("components.ManageAbsencesMobile.accepted")}</option>
                        <option value="Rejected">{t("components.ManageAbsencesMobile.rejected")}</option>
                      </Form.Select>
                    </>
                  )}
                </div>
              </div>

              <div
                className="list-container custom-scroll flex-grow-1"
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                {coworkerAbsences.length > 0 ? (
                  coworkerAbsences.map((absence) => (
                    <div
                      key={absence.id}
                      onClick={() => {
                        if (isPastAbsence(absence)) return;
                        handleWorkerClick(absence);
                      }}
                      className={`absence-row ${absence.status.toLowerCase()} d-flex justify-content-between align-items-center mb-2`}
                      style={{ cursor: "pointer" }}
                    >
                      <div>
                        <h6 className="txt-main m-0 fw-bold">
                          {getFullName(absence, t)} - {absence.absence_type}
                        </h6>
                        <small className="txt-main-50">
                          {renderAbsenceDate(absence)}
                        </small>
                      </div>

                      <div
                        className={`status-pill ${absence.status.toLowerCase()}`}
                      >
                        {absence.status}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-white-50 mt-3 px-2">
                    {t("components.ManageAbsencesMobile.no_absences")}
                  </p>
                )}
              </div>
            </div>
          </Col>
        </Row>

        <RequestAbsenceModal
          show={showModal}
          handleClose={handleClose}
          newAbsence={newAbsence}
          handleInputChange={handleInputChange}
          handleRequestAbsence={handleRequestAbsence}
          setNewAbsence={setNewAbsence}
          balance={balance}
          editMode={editMode}
          validationError={validationError}
          setValidationError={setValidationError}
          absences={absences}
          absenceToEdit={absenceToEdit}
        />

        <UserAbsenceModal
          show={showUserDetailModal}
          handleClose={() => {
            setShowUserDetailModal(false);
            setSelectedPendingAbsence(null);
          }}
          userProfile={normalizedUserProfile}
          currentUserId={user?.id}
          pendingAbsence={selectedPendingAbsence}
          isAdmin={isAdmin}
          holidays={workerHolidays}
          onAddAbsenceClick={() => handleShowForUser(selectedWorkerProfile.id)}
          onAccept={openAcceptConfirm}
          onReject={openRejectConfirm}
          onDelete={openDeleteConfirm}
        />

        <ConfirmationModal
          show={confirmAction.show}
          onHide={() => setConfirmAction({ ...confirmAction, show: false })}
          onConfirm={confirmAction.onConfirm}
          title={confirmAction.title}
          message={confirmAction.message}
          showReason={confirmAction.showReason}
          variant={confirmAction.variant}
        />
      </Container>
    </div>
  );
};

export default AbsencesPage;